import type { ConvexRuleKey, RuleState } from './govern-rules';

export type GovernanceRuleFamily =
  | 'styles'
  | 'tokens'
  | 'components'
  | 'a11y'
  | 'safety'
  | 'brand'
  | 'tailwind'
  | 'other';

export interface GovernanceRuleCatalogEntry {
  label: string;
  family: GovernanceRuleFamily;
  policyKey?: ConvexRuleKey;
}

export const GOVERNANCE_RULE_CATALOG: Record<
  string,
  GovernanceRuleCatalogEntry
> = {
  'styles/no-raw-color': {
    label: 'Raw Colors',
    family: 'styles',
    policyKey: 'tokens_hardcoded_values',
  },
  'styles/no-raw-spacing': {
    label: 'Off-Scale Spacing',
    family: 'styles',
    policyKey: 'tokens_hardcoded_values',
  },
  'tailwind/arbitrary-color': {
    label: 'Tailwind Arbitrary Colors',
    family: 'tailwind',
    policyKey: 'tailwind_arbitrary_color',
  },
  'tailwind/arbitrary-spacing': {
    label: 'Tailwind Arbitrary Spacing',
    family: 'tailwind',
    policyKey: 'tailwind_arbitrary_spacing',
  },
  'tailwind/forbidden-palette': {
    label: 'Tailwind Forbidden Palette',
    family: 'tailwind',
    policyKey: 'tailwind_forbidden_palette',
  },
  'tailwind/off-scale-spacing-token': {
    label: 'Tailwind Off-Scale Spacing',
    family: 'tailwind',
    policyKey: 'tailwind_off_scale_spacing_token',
  },
  'tailwind/raw-color-via-token': {
    label: 'Tailwind Raw Color Tokens',
    family: 'tailwind',
    policyKey: 'tailwind_raw_color_via_token',
  },
  'tailwind/unknown-class': {
    label: 'Tailwind Unknown Classes',
    family: 'tailwind',
    policyKey: 'tailwind_unknown_class',
  },
  'components/unknown-prop': {
    label: 'Unknown Props',
    family: 'components',
    policyKey: 'components_usage',
  },
  'components/forbidden-prop-value': {
    label: 'Forbidden Prop Values',
    family: 'components',
    policyKey: 'components_usage',
  },
  'components/prefer-library': {
    label: 'Prefer Library Components',
    family: 'components',
    policyKey: 'components_prefer_library',
  },
  'components/preferred-component': {
    label: 'Preferred Components',
    family: 'components',
    policyKey: 'components_usage',
  },
  'a11y/required-accessible-name': {
    label: 'Missing Accessible Names',
    family: 'a11y',
    policyKey: 'a11y_wcag',
  },
  'tokens/hardcoded-value': {
    label: 'Hardcoded Values',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'tokens/hardcoded-values': {
    label: 'Hardcoded Values',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'tokens/require-design-tokens': {
    label: 'Required Design Tokens',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'tokens/duplicate-value': {
    label: 'Duplicate Tokens',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'tokens/allowed-prefixes': {
    label: 'Allowed Token Prefixes',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'tokens/require-dual-fallback': {
    label: 'Token Fallbacks',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'theme/no-theme-coupled-literal': {
    label: 'Theme-Coupled Literals',
    family: 'tokens',
    policyKey: 'tokens_hardcoded_values',
  },
  'a11y/standard': {
    label: 'Accessibility',
    family: 'a11y',
    policyKey: 'a11y_wcag',
  },
  'a11y/wcag': {
    label: 'Accessibility',
    family: 'a11y',
    policyKey: 'a11y_wcag',
  },
  'components/usage': {
    label: 'Component Usage',
    family: 'components',
    policyKey: 'components_usage',
  },
};

const FAMILY_LABELS: Record<GovernanceRuleFamily, string> = {
  styles: 'Style',
  tokens: 'Token',
  components: 'Component',
  a11y: 'Accessibility',
  safety: 'Safety',
  brand: 'Brand',
  tailwind: 'Tailwind',
  other: 'Governance',
};

export type PolicyRules = Record<
  string,
  { enabled?: boolean; severity?: string; options?: Record<string, unknown> }
>;

export type PolicyCodes = Record<string, RuleState | undefined>;

export interface PolicyConfig {
  rules?: PolicyRules | null;
  codes?: PolicyCodes | null;
}

export function getRuleCatalogEntry(
  ruleId: string,
): GovernanceRuleCatalogEntry | undefined {
  return GOVERNANCE_RULE_CATALOG[ruleId];
}

export function getRulePolicyKey(ruleId: string): ConvexRuleKey | undefined {
  return getRuleCatalogEntry(ruleId)?.policyKey;
}

export function getRuleFamily(ruleId: string): GovernanceRuleFamily {
  const catalogFamily = getRuleCatalogEntry(ruleId)?.family;
  if (catalogFamily) return catalogFamily;

  const prefix = ruleId.split('/')[0];
  if (
    prefix === 'styles' ||
    prefix === 'tokens' ||
    prefix === 'components' ||
    prefix === 'a11y' ||
    prefix === 'safety' ||
    prefix === 'brand' ||
    prefix === 'tailwind'
  ) {
    return prefix;
  }
  return 'other';
}

export function getRuleFamilyLabel(ruleId: string): string {
  return FAMILY_LABELS[getRuleFamily(ruleId)];
}

export function formatRuleLabel(ruleId: string): string {
  const catalogLabel = getRuleCatalogEntry(ruleId)?.label;
  if (catalogLabel) return catalogLabel;

  const last = ruleId.split('/').pop() ?? ruleId;
  return last
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export function formatRuleLabelSentence(ruleId: string): string {
  const label = formatRuleLabel(ruleId);
  return label.charAt(0).toLowerCase() + label.slice(1);
}

export function isRuleEnabledByPolicy(
  ruleId: string,
  policyRules: PolicyRules | null,
): boolean {
  if (!policyRules) return true;
  const policyKey = getRulePolicyKey(ruleId);
  if (!policyKey) return true;
  return policyRules[policyKey]?.enabled ?? true;
}

export function getFindingPolicyState(
  input: { ruleId: string; code?: string },
  policy: PolicyConfig | PolicyRules | null,
): RuleState | { enabled?: boolean; severity?: string } | undefined {
  if (!policy) return undefined;
  if ('codes' in policy || 'rules' in policy) {
    const config = policy as PolicyConfig;
    if (input.code && config.codes?.[input.code]) {
      return config.codes[input.code];
    }
    const policyKey = getRulePolicyKey(input.ruleId);
    return policyKey
      ? (config.rules?.[policyKey] as RuleState | undefined)
      : undefined;
  }
  const policyKey = getRulePolicyKey(input.ruleId);
  return policyKey ? (policy as PolicyRules)[policyKey] : undefined;
}

export function isFindingEnabledByPolicy(
  input: { ruleId: string; code?: string },
  policy: PolicyConfig | PolicyRules | null,
): boolean {
  return getFindingPolicyState(input, policy)?.enabled ?? true;
}
